import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';

// --- CONSTANTS ---
export const COMPLETED_STATUS = 'completed';
export const DEFAULT_TASKS_PER_PAGE = 10;
export const EXPAND_THRESHOLD = 5;

// --- TYPE DEFINITIONS ---

export interface CompletedTask {
  id: string;
  patient: string;
  type: string;
  provider: string;
  completedAt?: string;
  duration?: string;
}

interface CompletedTaskItemProps {
  task: CompletedTask;
  onReviewClick?: (task: CompletedTask) => void;
  onMenuClick?: (task: CompletedTask) => void;
  className?: string;
}

interface CompletedTasksCardProps {
  title?: string;
  subtitle?: string;
  tasks?: CompletedTask[];
  expandable?: boolean;
  initialVisibleCount?: number;
  onReviewClick?: (task: CompletedTask) => void;
  onMenuClick?: (task: CompletedTask) => void;
  onExpandToggle?: (isExpanded: boolean) => void;
  className?: string;
}

// --- SUB-COMPONENTS ---

const CompletedTaskItem: React.FC<CompletedTaskItemProps> = ({ 
  task,
  onReviewClick = () => {},
  onMenuClick = () => {},
  className = ''
}) => {
  if (!task || !task.id) {
    console.warn('CompletedTaskItem: task object with id is required');
    return null;
  }

  return (
    <div 
      className={cn(
        'flex items-center justify-between py-3 px-4 border-b border-gray-100 hover:bg-gray-50 transition-colors',
        className
      )}
      role="listitem"
    >
      <div className="flex items-center flex-1 min-w-0">
        <div className="grid grid-cols-3 gap-8 w-full items-center">
          <div className="min-w-0">
            <p className="text-paragraph font-medium text-gray-900 truncate">{task.patient}</p>
          </div>
          <div className="min-w-0">
            <p className="text-paragraph font-light text-gray-600 truncate">{task.type}</p>
          </div>
          <div className="min-w-0">
            <p className="text-paragraph font-light text-gray-500 truncate">{task.provider}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-6 flex-shrink-0">
        <Badge className="bg-emerald-100 text-emerald-800 border-0 text-caption font-medium px-3 py-1 rounded-full">
          Completed task
        </Badge>
        <Button variant="outline" size="sm" onClick={() => onReviewClick(task)}>
          Review
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onMenuClick(task)}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

CompletedTaskItem.displayName = 'CompletedTaskItem';

// --- MAIN COMPONENT ---

const CompletedTasksCard: React.FC<CompletedTasksCardProps> = ({
  title = 'Self-Completed Tasks',
  subtitle,
  tasks = [],
  expandable = true,
  initialVisibleCount = EXPAND_THRESHOLD,
  onReviewClick = () => {},
  onMenuClick = () => {},
  onExpandToggle = () => {},
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onExpandToggle(newExpandedState);
  };

  const shouldShowExpandButton = expandable && tasks.length > initialVisibleCount;
  const visibleTasks = isExpanded ? tasks : tasks.slice(0, initialVisibleCount);

  return (
    <div className={cn('bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100', className)}>
      <div className="px-6 py-5 flex justify-between items-start">
        <div>
          <h2 className="text-h5 font-semibold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="text-paragraph font-light text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        {shouldShowExpandButton && (
          <Button
            variant="ghost"
            size="sm"
            className="text-sky-600 hover:text-sky-700 text-button font-medium"
            onClick={handleExpandToggle}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        )}
      </div>

      <div role="list" aria-label="Completed tasks">
        {visibleTasks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-paragraph font-light text-gray-500">No completed tasks to display</p>
          </div>
        ) : (
          visibleTasks.map((task, index) => (
            <CompletedTaskItem
              key={task.id}
              task={task}
              onReviewClick={onReviewClick}
              onMenuClick={onMenuClick}
              className={index === visibleTasks.length - 1 ? 'border-b-0' : ''}
            />
          ))
        )}
      </div>

      {tasks.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-caption font-light text-gray-500">
              {isExpanded || !shouldShowExpandButton 
                ? `Showing all ${tasks.length} completed tasks`
                : `Showing ${visibleTasks.length} of ${tasks.length} completed tasks`
              }
            </p>
            {shouldShowExpandButton && (
              <Button
                variant="ghost"
                size="sm"
                className="text-sky-600 hover:text-sky-700 text-caption font-medium"
                onClick={handleExpandToggle}
              >
                {isExpanded 
                  ? `Show less` 
                  : `Show ${tasks.length - initialVisibleCount} more`
                }
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CompletedTasksCard.displayName = 'CompletedTasksCard';

export default CompletedTasksCard;
